import { Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RemovePTags } from '@common/pipes/remove-p-tag.pipe';
import { CountryISO, PhoneNumberFormat, SearchCountryField, NgxIntlTelInputModule } from '@moddi3/ngx-intl-tel-input';
import { TranslateModule } from '@ngx-translate/core';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { ChatBotsButtonSettings, FormWidgetData } from '@web/widgets/form/type';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';
import { EButtonStyleType, EFormInputType, ESize, ESocialNetworks } from '@common/enums';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import type { FormElement } from '@common/types/form-element.type';
import { NgxDropzoneModule } from 'ngx-dropzone';
import type { Option } from '@common/types/form-element.type';
import type { FormButtonSettings } from '@components/sp-form-button-settings';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import type { ChatBotData } from '@web/widgets/chat-bot/type';
import type { ThemedBaseButton } from '@common/types/base-button-widget.type';
import { socialIconTemplates } from '@common/icons/social-icons';
import {
    getFormChatBotCssClasses,
    getFormChatBotCssVars,
    getFormChatBotWrapperCssClasses,
    getFormModalBodyCssVars,
    getFormModalButtonCssClasses,
    getFormModalButtonCssVars,
    getFormModalInputCssClasses,
    getFormModalInputCssVars,
    getFormModalSizeCssVars,
    getFormRootCssVars,
} from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import type { FormCssVars } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import { SpIntlTelInputA11yDirective } from '@common/directives/intl-tel-input-a11y.directive';
import { FORM_ROOT_THEME_TOKENS, colorCss } from '@web/widgets/common/css-vars/css-vars.utils';

@Component({
    selector: 'web-form-modal-preview',
    templateUrl: './form-modal-preview.component.html',
    styleUrls: ['./form-modal-preview.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FormsModule,
        NgxIntlTelInputModule,
        SpIntlTelInputA11yDirective,
        TranslateModule,
        RemovePTags,
        NgxDropzoneModule,
    ],
})
export class FormModalPreviewComponent {
    @Input() public widget: WebLegacyWidgetEntity<FormWidgetData>;

    public readonly typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;
    public readonly SearchCountryField = SearchCountryField;
    public readonly PhoneNumberFormat = PhoneNumberFormat;

    public CountryISO = CountryISO.Ukraine;
    public excludeCountries = [CountryISO.Russia];

    private sanitizer: DomSanitizer = inject(DomSanitizer);

    public getSelected(options: Option[]) {
        return !options.some((el) => el.selected);
    }

    public get elements(): FormElement[] {
        return this.widget.data.elements;
    }

    public get inputSettings(): FormWidgetData['inputSettings'] {
        return this.widget.data.inputSettings;
    }

    public get formButtonSettings(): FormWidgetData['modalFormButtonSettings'] {
        return this.widget.data.modalFormButtonSettings;
    }

    public get addChatBotToForm(): FormWidgetData['addChatBotToForm'] {
        return this.widget.data.addChatBotToForm;
    }

    public get chatBots(): ChatBotData[] {
        return this.widget.data.chatBots;
    }

    public get chatBotsButtonSettings(): ThemedBaseButton & ChatBotsButtonSettings {
        return this.widget.data.chatBotsButtonSettings;
    }

    public get chatBotStyleType(): EButtonStyleType {
        return this.widget.data.chatBotsButtonSettings.styleType;
    }

    public get buttonSize(): ESize {
        return this.chatBotsButtonSettings.buttonSize;
    }

    /** Modal body + input theming + input-size variables (cascade to the field/control descendants). */
    public get modalCssVars(): FormCssVars {
        return {
            ...getFormRootCssVars(this.widget, FORM_ROOT_THEME_TOKENS),
            ...getFormModalBodyCssVars(this.inputSettings, colorCss),
            ...getFormModalInputCssVars(this.inputSettings, colorCss),
            ...getFormModalSizeCssVars(this.inputSettings),
        };
    }

    public get modalFieldCssClasses(): string {
        return getFormModalInputCssClasses(this.inputSettings);
    }

    public get modalButtonCssVars(): FormCssVars {
        return getFormModalButtonCssVars(this.formButtonSettings, colorCss);
    }

    public get chatBotCssVars(): FormCssVars {
        return getFormChatBotCssVars(this.chatBotsButtonSettings, colorCss);
    }

    public get chatBotWrapperCssClasses(): string {
        return getFormChatBotWrapperCssClasses(this.chatBotsButtonSettings, this.inputSettings);
    }

    public getButtonStyles(modalFormButtonSettings: FormButtonSettings): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[modalFormButtonSettings.size ? modalFormButtonSettings.size : ESize.M]} form-modal-button ${getFormModalButtonCssClasses(modalFormButtonSettings)}`;
        if (modalFormButtonSettings.styleType === EButtonStyleType.custom) {
            return styles;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[modalFormButtonSettings.styleType]}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public getStyleList(chatbot): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[this.buttonSize ? this.buttonSize : ESize.M]} form-chatbot-anchor ${getFormChatBotCssClasses(this.chatBotsButtonSettings)}`;
        if (this.chatBotStyleType === EButtonStyleType.custom) {
            return styles;
        }

        if (this.chatBotStyleType === EButtonStyleType.classic) {
            let chatBotBrandClass = '';

            switch (chatbot) {
                case ESocialNetworks.messenger:
                    chatBotBrandClass = 'chat-bot-messenger';
                    break;
                case ESocialNetworks.instagram:
                    chatBotBrandClass = 'chat-bot-instagram';
                    break;
                case ESocialNetworks.telegram:
                    chatBotBrandClass = 'chat-bot-telegram';
                    break;
                case ESocialNetworks.viber:
                    chatBotBrandClass = 'chat-bot-viber';
                    break;
            }

            return `${styles} ${BUTTON_TYPE_CLASS_MAP[EButtonStyleType.primary]} ${chatBotBrandClass}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[this.chatBotStyleType]}`;
    }

    public getChatBotIcon(iconName: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(socialIconTemplates[iconName]);
    }
}
